import type { ReactNode } from 'react';
import {
    BracketsCurlyIcon,
    ChartBarIcon,
    ExportIcon,
    FileDocIcon,
    FilePdfIcon,
    FileZipIcon,
    GlobeIcon,
    NotePencilIcon,
    PlusIcon,
    StackSimpleIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
    ActionBar,
    AnimatedActionBarModeTransition,
    ConfidenceFilter,
    SelectionBar,
} from '@/components';
import type { ActionConfig } from '@/components/ActionBar';
import {
    ActionBarInteractionTrigger,
    ActionBarLabelsContext,
    ActionType,
    DividerOrientation,
    buildFiltersAction,
    buildSortAction,
    buildMultiSelectToggle,
} from '@/components/ActionBar';
import { ActiveIndicator } from '@/components/ActionBar';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { usePopoverCloseSignal } from '@/stores';
import {
    REPORT_EVENT_SORT_OPTIONS,
    REPORT_GROUP_BY_OPTIONS,
    type ReportConfidenceRange,
    type ReportEventSortOption,
    type ReportGroupByMode,
} from '../constants';

interface ReportActionBarProps {
    identityFilter: ReactNode;
    isIdentityFilterActive: boolean;
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder: string;
    groupBy: ReportGroupByMode;
    onGroupByChange: (mode: ReportGroupByMode) => void;
    sortOption: ReportEventSortOption;
    onSortOptionChange: (option: ReportEventSortOption) => void;
    confidence: ReportConfidenceRange;
    onConfidenceApply: (range: ReportConfidenceRange) => void;
    onConfidenceClear: () => void;
    onAddEvents: () => void;
    onEditReport: () => void;
    onDeleteReport: () => void;
    isExportEnabled: boolean;
    isExporting: boolean;
    isZipExporting: boolean;
    zipExportEnabled: boolean;
    onExportPdf: () => void;
    onExportDoc: () => void;
    onExportJson: () => void;
    onExportZip: () => void;
    isMultiSelectMode: boolean;
    selectedCount: number;
    totalCount: number;
    onEnterMultiSelect: () => void;
    onExitMultiSelect: () => void;
    onSelectAll: () => void;
    selectionBulkActions: ActionConfig[];
}

export function ReportActionBar({
    identityFilter,
    isIdentityFilterActive,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    groupBy,
    onGroupByChange,
    sortOption,
    onSortOptionChange,
    confidence,
    onConfidenceApply,
    onConfidenceClear,
    onAddEvents,
    onEditReport,
    onDeleteReport,
    isExportEnabled,
    isExporting,
    isZipExporting,
    zipExportEnabled,
    onExportPdf,
    onExportDoc,
    onExportJson,
    onExportZip,
    isMultiSelectMode,
    selectedCount,
    totalCount,
    onEnterMultiSelect,
    onExitMultiSelect,
    onSelectAll,
    selectionBulkActions,
}: ReportActionBarProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { t: translateCommon } = useTranslation(I18nNamespace.COMMON);
    const triggerPopoverClose = usePopoverCloseSignal((state) => state.trigger);

    const currentSortId =
        sortOption.field === 'manual'
            ? 'manual'
            : `${sortOption.field}-${sortOption.direction}`;

    const buildExportAction = (disabled: boolean): ActionConfig => ({
        type: ActionType.MENU,
        icon: ExportIcon,
        label: isExporting ? t('detail.export.exporting') : t('actions.export'),
        trigger: ActionBarInteractionTrigger.CLICK,
        title: isExporting
            ? t('detail.export.exporting')
            : isExportEnabled
              ? t('detail.export.title')
              : t('detail.export.unavailable'),
        disabled: disabled || !isExportEnabled || isExporting,
        items: [
            {
                icon: FilePdfIcon,
                label: t('detail.export.pdf'),
                onClick: onExportPdf,
            },
            {
                icon: FileDocIcon,
                label: t('detail.export.doc'),
                onClick: onExportDoc,
            },
            {
                icon: BracketsCurlyIcon,
                label: t('detail.export.json'),
                onClick: onExportJson,
            },
            {
                icon: FileZipIcon,
                label: !zipExportEnabled
                    ? t('detail.export.zipUnavailable')
                    : isZipExporting
                      ? t('detail.export.zipExporting')
                      : t('detail.export.zip'),
                onClick: onExportZip,
                disabled: !zipExportEnabled,
            },
        ],
    });

    const groupByActive = groupBy !== 'none';
    const actions: ActionConfig[] = [
        {
            type: ActionType.SEARCH,
            value: searchValue,
            onChange: onSearchChange,
            placeholder: searchPlaceholder,
        },
        {
            type: ActionType.MENU,
            icon: StackSimpleIcon,
            label: translateCommon('browseBar.groupBy'),
            trigger: ActionBarInteractionTrigger.CLICK,
            title: t('detail.groupBy.menuTitle'),
            active: groupByActive,
            onClear: groupByActive ? () => onGroupByChange('none') : undefined,
            items: REPORT_GROUP_BY_OPTIONS.map((option) => ({
                label: t(option.labelKey),
                isActive: groupBy === option.id,
                onClick: () => onGroupByChange(option.id),
            })),
        },
        buildFiltersAction(translateCommon, {
            isActive: true,
            items: [
                {
                    icon: ChartBarIcon,
                    label: translateCommon('browseBar.confidenceLevel'),
                    isActive: true,
                    children: (
                        <ConfidenceFilter
                            min={confidence.min}
                            onApply={onConfidenceApply}
                            onClear={onConfidenceClear}
                            onClose={triggerPopoverClose}
                        />
                    ),
                },
                {
                    icon: GlobeIcon,
                    label: translateCommon('browseBar.identity'),
                    isActive: isIdentityFilterActive,
                    activeIndicator: ActiveIndicator.DOT,
                    children: identityFilter,
                },
            ],
        }),
        buildSortAction(
            translateCommon,
            REPORT_EVENT_SORT_OPTIONS.map((option) => ({
                label: t(option.labelKey),
                isActive: currentSortId === option.id,
                onClick: () =>
                    onSortOptionChange({
                        field: option.field,
                        direction: option.direction,
                    }),
            })),
        ),
        buildMultiSelectToggle(translateCommon, onEnterMultiSelect),
        { type: ActionType.DIVIDER, orientation: DividerOrientation.VERTICAL },
        buildExportAction(false),
        {
            type: ActionType.ITEM,
            icon: NotePencilIcon,
            label: t('detail.editReport'),
            onClick: onEditReport,
        },
        {
            type: ActionType.ITEM,
            icon: TrashIcon,
            label: t('detail.deleteReport'),
            onClick: onDeleteReport,
        },
        {
            type: ActionType.ITEM,
            icon: PlusIcon,
            label: t('detail.addEvents'),
            onClick: onAddEvents,
        },
    ];

    return (
        <ActionBarLabelsContext.Provider value="wide">
            <div className="w-fit">
                <AnimatedActionBarModeTransition
                    isMultiSelectMode={isMultiSelectMode}
                    browseSlot={<ActionBar actions={actions} />}
                    selectionSlot={
                        <SelectionBar
                            selectedCount={selectedCount}
                            totalCount={totalCount}
                            entityLabel={t('detail.eventsEntity')}
                            onSelectAll={onSelectAll}
                            onExitMultiSelect={onExitMultiSelect}
                            bulkActions={[
                                buildExportAction(selectedCount === 0),
                                ...selectionBulkActions,
                            ]}
                        />
                    }
                />
            </div>
        </ActionBarLabelsContext.Provider>
    );
}
